import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../../store/useUserStore';
import { useAnalysisStore } from '../../store/useAnalysisStore';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, ON_KIDS_MAIN_KID_ID } from '../../data/define';
import Url from '../../data/url';
import { useSignoutMutation } from '../../queries';
import { TextField } from '@mui/material';
import toast from 'react-hot-toast';
import IcProfile from '../../assets/image/ic_profile.svg';
import IcPlus from '../../assets/image/ic_plus_gray_circle.svg';
import Divider from '../common/Divider';
import BottomTwoBtnDialog from '../common/BottomTwoBtnDialog';
import TransparentTwoBtnDialog from '../common/TransparentTwoBtnDialog';
import OneBtnAlert from '../common/OneBtnAlert';

function ParentProfileEdit({
  userProfile,
  profileFile = null,
  setProfileFile,
  nickName = '',
  setNickName,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { history } = location.state || {};
  const setUser = useUserStore((s) => s.setUser);
  const resetAnalysis = useAnalysisStore((s) => s.resetAnalysisResult);
  const controller = useAnalysisStore((s) => s.abortController);
  const setController = useAnalysisStore((s) => s.setAbortController);
  const fileRef = useRef();
  const nameRef = useRef(null);
  const birthDateRef = useRef(null);
  const phoneNumberRef = useRef(null);
  const [imageBtnDialogOpen, setImageBtnDialogOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [signoutDialogOpen, setSignoutDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [oneBtnAlertOpen, setOneBtnAlertOpen] = useState(false);

  const { mutate: signout } = useSignoutMutation();

  useEffect(() => {
    if (userProfile) {
      setName(userProfile.name || '');
      setBirthDate(userProfile.birth || '');
      setPhoneNumber(userProfile.phoneNumber || '');
    }
  }, [userProfile]);

  const handleAddImage = useCallback(() => {
    (fileRef.current as any)?.click();
  }, [fileRef]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.size > 5 * 1024 * 1024) {
      toast.error('5MB 이하의 이미지로 올려주세요.');
      e.target.value = '';
      return;
    }
    if (!file) return;
    setProfileFile({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
    });
    e.target.value = '';
  };

  useEffect(() => {
    if (isFocused) {
      setAuthDialogOpen(true);
      nameRef.current?.blur();
      birthDateRef.current?.blur();
      phoneNumberRef.current?.blur();
    }
  }, [isFocused]);

  const handleCloseAuthDialog = () => {
    setAuthDialogOpen(false);
    setIsFocused(false);
  };

  const handleLoginPass = () => {
    handleCloseAuthDialog();
    // localStorage.removeItem(ACCESS_TOKEN_KEY);
    // localStorage.removeItem(REFRESH_TOKEN_KEY);
    navigate(`${Url.LoginPass}?type=${userProfile.loginType}&history=profile`);
  };

  const handleLogout = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ON_KIDS_MAIN_KID_ID);
    setLogoutDialogOpen(false);
    // 다른 계정으로 로그인했을 때 이전 계정의 아이·분석 정보가 남지 않게 비운다(user-48)
    setUser((prev) => ({ ...prev, isLoggedIn: false, kids: [], mainKid: null }));
    resetAnalysis();
    queryClient.clear();
    if (controller) {
      controller.abort();
      setController(null);
    }
  };

  const handleSignout = () => {
    signout(null, {
      onSuccess: () => {
        setSignoutDialogOpen(false);
        localStorage.clear();
        setUser((prev) => ({ ...prev, isLoggedIn: false, kids: [], mainKid: null }));
        resetAnalysis();
        queryClient.clear();
        navigate(Url.Root, { replace: true });
      },
      onError: (error) => {
        console.error('Signout failed:', error);
        toast.error('회원 탈퇴에 실패했습니다. 다시 시도해주세요.');
      },
    });
  };

  useEffect(() => {
    if (history === 'login') {
      setOneBtnAlertOpen(true);
    }
  }, [history]);

  return (
    <div className="w-full flex flex-col items-center justify-center py-[24px] rounded-[24px]">
      <div className="relative bg-white flex items-center justify-center mb-[16px]">
        <input
          type="file"
          ref={fileRef}
          accept="image/png"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        <img
          src={profileFile && profileFile.url ? profileFile.url : IcProfile}
          alt="thumnail1"
          className="w-[100px] h-[100px] border overflow-hidden rounded-[40px] border-[#EAEAEA]"
        />
        <button
          type="button"
          className="absolute bottom-[12px] right-[12px] translate-x-[50%] translate-y-[50%]"
          onClick={() => setImageBtnDialogOpen(true)}
          style={{ zIndex: 1 }}
        >
          <img src={IcPlus} alt="IcPlus" />
        </button>
        <TransparentTwoBtnDialog
          open={imageBtnDialogOpen}
          onClose={() => setImageBtnDialogOpen(false)}
          leftBtnTitle="사진 파일 선택"
          rightBtnTitle="닫기"
          onLeftClick={() => {
            setImageBtnDialogOpen(false);
            handleAddImage();
          }}
          onRightClick={() => {
            setImageBtnDialogOpen(false);
          }}
        />
      </div>
      <div className="w-full text-[12px] text-[#7D7D7D] mt-[10px] flex items-center justify-center gap-2 mb-[24px]">
        <TextField
          value={nickName}
          onChange={(e) => setNickName(e.target.value)}
          label="닉네임"
          variant="standard"
          fullWidth
          inputProps={{ maxLength: 10 }}
        />
      </div>
      <Divider />
      <div className="w-full text-[12px] text-[#7D7D7D] mb-[28px] flex flex-col gap-[28px] items-center justify-center mt-[24px]">
        <TextField
          inputRef={nameRef}
          value={name}
          label="이름"
          variant="standard"
          fullWidth
          onFocus={() => setIsFocused(true)}
        />
        <TextField
          inputRef={birthDateRef}
          value={birthDate}
          label="생년월일"
          variant="standard"
          fullWidth
          onFocus={() => setIsFocused(true)}
        />
        <TextField
          inputRef={phoneNumberRef}
          value={phoneNumber}
          label="전화번호"
          variant="standard"
          fullWidth
          onFocus={() => setIsFocused(true)}
        />
      </div>
      <BottomTwoBtnDialog
        open={authDialogOpen}
        onClose={handleCloseAuthDialog}
        title="본인인증이 필요해요"
        description={`이름, 생년월일, 휴대폰번호는\n본인인증 정보로만 수정 가능해요`}
        leftBtnText="취소"
        rightBtnText="본인인증"
        leftBtnClick={() => {
          handleCloseAuthDialog();
        }}
        rightBtnClick={() => {
          handleLoginPass();
        }}
      />
      <div className="w-full px-[15px] py-[12px] font-[400] text-[14px] mb-[40px] rounded-[10px] bg-[#F8F8F8]">
        위 정보는 본인인증 후 수정 가능해요
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className="underline font-[400] text-[12px] text-[#969696]"
          onClick={() => setLogoutDialogOpen(true)}
        >
          로그아웃
        </button>
        <BottomTwoBtnDialog
          open={logoutDialogOpen}
          onClose={() => setLogoutDialogOpen(false)}
          title="로그아웃 하시겠어요?"
          description=""
          leftBtnText="취소"
          rightBtnText="로그아웃"
          leftBtnClick={() => {
            setLogoutDialogOpen(false);
          }}
          rightBtnClick={() => {
            handleLogout();
          }}
        />
        <button
          type="button"
          className="underline font-[400] text-[12px] text-[#969696]"
          onClick={() => setSignoutDialogOpen(true)}
        >
          탈퇴하기
        </button>
        <OneBtnAlert
          open={oneBtnAlertOpen}
          onClose={() => setOneBtnAlertOpen(false)}
          title="회원 정보가 변경되었어요"
          description={`본인 인증된 정보로\n회원 정보가 변경되었습니다.`}
          btnText="확인"
          onClick={() => setOneBtnAlertOpen(false)}
        />
        <BottomTwoBtnDialog
          open={signoutDialogOpen}
          onClose={() => setSignoutDialogOpen(false)}
          title="탈퇴하기 전에 확인해 주세요."
          description="회원탈퇴 시 회원 및 아이의 모든 정보가 삭제됩니다. 탈퇴하시겠어요?"
          leftBtnText="취소"
          rightBtnText="탈퇴하기"
          leftBtnClick={() => {
            setSignoutDialogOpen(false);
          }}
          rightBtnClick={() => {
            handleSignout();
          }}
        />
      </div>
    </div>
  );
}

export default ParentProfileEdit;
